import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { AuthShell } from "@/components/AuthShell";
import { Button } from "@/components/ui/button";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function VerifyEmail() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token");
  const email = params.get("email") || "";
  const next = params.get("next") || "/";
  const { setUser } = useAuth();
  const [status, setStatus] = useState(token ? "verifying" : "pending");

  const verifiedToken = useRef(null);

  useEffect(() => {
    if (!token) return;
    // Una sola chiamata per token: in StrictMode l'effetto viene eseguito due
    // volte e la seconda richiesta mostrava "Link non valido" su una verifica
    // in realta riuscita.
    if (verifiedToken.current === token) return;
    verifiedToken.current = token;
    api
      .post("/auth/verify-email", { token })
      .then((r) => {
        setUser({ ...r.data, email_verified: true });
        setStatus("verified");
      })
      .catch(() => setStatus("failed"));
  }, [token, setUser]);

  const resend = async () => {
    if (!email) {
      toast.error(
        "Non conosciamo l'indirizzo da verificare. Apri di nuovo la registrazione o il login per richiedere un nuovo link."
      );
      return;
    }
    try {
      await api.post("/auth/resend-verification", { email, next_path: next });
      toast.success("E-mail di verifica inviata di nuovo.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  if (status === "verified") {
    return (
      <AuthShell testid="verify-verified-card" title="E-mail verificata" subtitle="Il tuo account è attivo.">
        <Button
          data-testid="verify-continue-btn"
          className="w-full bg-[#e8836b] hover:bg-[#d76f57] text-white rounded-full py-6 text-sm font-semibold"
          onClick={() => navigate(next)}
        >
          CONTINUA
        </Button>
      </AuthShell>
    );
  }

  if (status === "verifying") {
    return (
      <AuthShell testid="verify-verifying-card" title="Verifica in corso…" subtitle="Attendi un istante." >
        <div className="h-2" />
      </AuthShell>
    );
  }

  if (status === "failed") {
    return (
      <AuthShell
        testid="verify-failed-card"
        title="Link non valido o scaduto"
        subtitle="Richiedi un nuovo link di verifica."
      >
        <Button
          data-testid="verify-resend-btn"
          className="w-full bg-[#e8836b] hover:bg-[#d76f57] text-white rounded-full py-6 text-sm font-semibold"
          onClick={resend}
        >
          INVIA DI NUOVO L’E-MAIL DI VERIFICA
        </Button>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      testid="verify-pending-card"
      title="VERIFICA LA TUA E-MAIL"
      subtitle={email ? `Abbiamo inviato un link a ${email}.` : "Abbiamo inviato un link di verifica alla tua e-mail."}
    >
      <div className="flex flex-col gap-3">
        <Button
          data-testid="verify-resend-btn"
          className="w-full bg-[#e8836b] hover:bg-[#d76f57] text-white rounded-full py-6 text-sm font-semibold"
          onClick={resend}
        >
          INVIA DI NUOVO L’E-MAIL DI VERIFICA
        </Button>
        <Button
          data-testid="verify-to-login-btn"
          variant="outline"
          className="w-full rounded-full py-5 border-[#e0d4c4]"
          onClick={() => navigate("/login")}
        >
          Vai al login
        </Button>
      </div>
    </AuthShell>
  );
}
